import React from 'react';
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AXIS_PROPS, GRID_PROPS, NO_ANIM, TOOLTIP_PROPS } from '../../chartTheme';

export interface TrendSeries {
  key: string;
  name: string;
  data: (number | undefined)[];
  color: string;
  /** Reference line (e.g. 7.0h sleep target) — dashed, no dots, no tooltip noise. */
  reference?: boolean;
  /** Filled area under the line. */
  area?: boolean;
}

export const TrendChart: React.FC<{
  labels: string[];
  series: TrendSeries[];
  min: number;
  max: number;
  height: number;
  unit?: string;
}> = ({ labels, series, min, max, height, unit = '' }) => {
  const rows = labels.map((label, i) => {
    const row: Record<string, string | number | undefined> = { label };
    series.forEach((s) => {
      row[s.key] = s.data[i];
    });
    return row;
  });
  return (
    <ResponsiveContainer width='100%' height={height}>
      <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
        <CartesianGrid {...GRID_PROPS} />
        <XAxis dataKey='label' {...AXIS_PROPS} />
        <YAxis domain={[min, max]} {...AXIS_PROPS} />
        <Tooltip {...TOOLTIP_PROPS} formatter={(v) => `${String(v)}${unit}`} />
        {series.map((s) =>
          s.area ? (
            <Area
              key={s.key}
              dataKey={s.key}
              name={s.name}
              type='monotone'
              stroke={s.color}
              strokeWidth={2}
              fill={s.color}
              fillOpacity={0.12}
              dot={false}
              {...NO_ANIM}
            />
          ) : (
            <Line
              key={s.key}
              dataKey={s.key}
              name={s.name}
              type='monotone'
              stroke={s.color}
              strokeWidth={s.reference ? 1.5 : 2}
              strokeDasharray={s.reference ? '4 4' : undefined}
              strokeOpacity={s.reference ? 0.6 : 1}
              dot={false}
              activeDot={s.reference ? false : undefined}
              {...NO_ANIM}
            />
          ),
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
